import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Eye,
  Package,
  Tag,
  DollarSign,
  Box
} from 'lucide-react';
import toast from 'react-hot-toast';
import ConfirmModal from '../../components/ui/ConfirmModal';

const AdminProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, productId: null });
  const [formData, setFormData] = useState({
    name_fr: '',
    name_en: '',
    description_fr: '',
    description_en: '',
    short_description_fr: '',
    short_description_en: '',
    price: '',
    sale_price: '',
    discount_percentage: '',
    cost_price: '',
    sku: '',
    category_id: '',
    in_stock: '',
    min_stock_level: '',
    is_active: true,
    is_new: false,
    is_featured: false,
    weight: '',
    dimensions: '',
    material: '',
    color: '',
    gemstone: '',
    reference: ''
  });

  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [photoPreview, setPhotoPreview] = useState([]);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [currentPage, searchTerm, selectedCategory, selectedStatus]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: currentPage,
        limit: 20,
        search: searchTerm,
        category: selectedCategory,
        status: selectedStatus
      });

      const response = await fetch(`/api/admin/products?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Products data received:', data.products);
        // Ensure price fields are numbers
        const processedProducts = data.products.map(product => ({
          ...product,
          price: typeof product.price === 'string' ? parseFloat(product.price) || 0 : product.price || 0,
          cost_price: typeof product.cost_price === 'string' ? parseFloat(product.cost_price) || 0 : product.cost_price || 0,
          in_stock: typeof product.in_stock === 'string' ? parseInt(product.in_stock) || 0 : product.in_stock || 0,
          min_stock_level: typeof product.min_stock_level === 'string' ? parseInt(product.min_stock_level) || 0 : product.min_stock_level || 0
        }));
        console.log('Processed products:', processedProducts);
        setProducts(processedProducts);
        setTotalPages(data.pagination.pages);
      } else {
        toast.error('Erreur lors du chargement des produits');
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Erreur lors du chargement des produits');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/admin/categories', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories);
      } else {
        toast.error('Erreur lors du chargement des catégories');
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Erreur lors du chargement des catégories');
    }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/admin/products', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Produit créé avec succès');
        setShowCreateModal(false);
        resetForm();
        fetchProducts();
      } else {
        const error = await response.json();
        toast.error(error.message || 'Erreur lors de la création');
      }
    } catch (error) {
      console.error('Error creating product:', error);
      toast.error('Erreur lors de la création');
    }
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    
    console.log('Update product submission started');
    console.log('Selected photos:', selectedPhotos);
    console.log('Photo previews:', photoPreview);
    
    try {
      const token = localStorage.getItem('token');
      const formDataToSend = new FormData();
      
      // Add form data
      Object.keys(formData).forEach(key => {
        const value = formData[key];
        // Handle boolean values properly for FormData
        if (typeof value === 'boolean') {
          formDataToSend.append(key, value ? '1' : '0');
          console.log(`Added form field: ${key} = ${value ? '1' : '0'} (boolean: ${value})`);
        } else {
          formDataToSend.append(key, value);
          console.log(`Added form field: ${key} = ${value} (type: ${typeof value})`);
        }
      });
      
      // Add new photos
      selectedPhotos.forEach((photo, index) => {
        formDataToSend.append('photos', photo);
        console.log(`Added new photo ${index + 1}:`, photo.name, photo.size, photo.type);
      });
      
      // Handle photo deletions
      const existingPhotos = photoPreview.filter(p => p.isExisting);
      const currentPhotos = editingProduct.photos || [];
      const deletedPhotos = currentPhotos.filter(currentPhoto => 
        !existingPhotos.some(existingPhoto => 
          existingPhoto.filename === currentPhoto.filename
        )
      );
      
      if (deletedPhotos.length > 0) {
        const deletePhotoNames = deletedPhotos.map(p => p.filename);
        formDataToSend.append('delete_photos', JSON.stringify(deletePhotoNames));
        console.log('Photos marked for deletion:', deletePhotoNames);
      }
      
      console.log('FormData prepared for update, sending request...');
      
      const response = await fetch(`/api/admin/products/${editingProduct.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend
      });
      
      console.log('Update response received:', response.status, response.statusText);
      
      if (response.ok) {
        const result = await response.json();
        console.log('Product updated successfully:', result);
        toast.success('Produit mis à jour avec succès');
        setShowEditModal(false);
        setEditingProduct(null);
        resetForm();
        fetchProducts();
      } else {
        const error = await response.json();
        console.error('Update server error:', error);
        toast.error(error.message || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Error updating product:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteProduct = async (productId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/admin/products/${productId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Produit supprimé avec succès');
        fetchProducts();
      } else {
        const error = await response.json();
        toast.error(error.message || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const openEditModal = (product) => {
    console.log('Opening edit modal for product:', product);
    setEditingProduct(product);
    setFormData({
      name_fr: product.name_fr || '',
      name_en: product.name_en || '',
      description_fr: product.description_fr || '',
      description_en: product.description_en || '',
      short_description_fr: product.short_description_fr || '',
      short_description_en: product.short_description_en || '',
      price: product.price || '',
      sale_price: product.sale_price || '',
      discount_percentage: product.sale_price && product.price
        ? Math.round(((product.price - product.sale_price) / product.price) * 100)
        : '',
      cost_price: product.cost_price || '',
      sku: product.sku || '',
      category_id: product.category_id || '',
      in_stock: product.in_stock || '',
      min_stock_level: product.min_stock_level || '',
      is_active: product.is_active === 1,
      is_new: product.is_new === 1,
      is_featured: product.is_featured === 1,
      weight: product.weight || '',
      dimensions: product.dimensions || '',
      material: product.material || '',
      color: product.color || '',
      gemstone: product.gemstone || '',
      reference: product.reference || ''
    });
    
    // Load existing photos
    if (product.photos && Array.isArray(product.photos)) {
      console.log('Loading existing photos:', product.photos);
      const existingPhotoPreviews = product.photos.map((photo, index) => ({
        id: `existing_${index}`,
        preview: photo.path,
        thumbnail: photo.thumbnail_path,
        filename: photo.filename,
        isExisting: true,
        photoData: photo
      }));
      setPhotoPreview(existingPhotoPreviews);
      setSelectedPhotos([]); // No new photos selected yet
    } else {
      console.log('No existing photos found');
      setPhotoPreview([]);
      setSelectedPhotos([]);
    }
    
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name_fr: '',
      name_en: '',
      description_fr: '',
      description_en: '',
      short_description_fr: '',
      short_description_en: '',
      price: '',
      sale_price: '',
      discount_percentage: '',
      cost_price: '',
      sku: '',
      category_id: '',
      in_stock: '',
      min_stock_level: '',
      is_active: true,
      is_new: false,
      is_featured: false,
      weight: '',
      dimensions: '',
      material: '',
      color: '',
      gemstone: '',
      reference: ''
    });
    setSelectedPhotos([]);
    setPhotoPreview([]);
  };

  // Photo handling functions
  const handlePhotoSelect = (event) => {
    const files = Array.from(event.target.files);
    setSelectedPhotos(prev => [...prev, ...files]);
    
    // Create previews
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoPreview(prev => [...prev, {
          file: file,
          preview: e.target.result,
          id: Date.now() + Math.random()
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (photoId) => {
    setPhotoPreview(prev => {
      const photo = prev.find(p => p.id === photoId);
      if (photo) {
        if (photo.isExisting) {
          // This is an existing photo, mark it for deletion
          console.log('Marking existing photo for deletion:', photo.filename);
          // We'll handle deletion in the update request
        } else {
          // This is a new photo, remove from selectedPhotos
          setSelectedPhotos(prevFiles => 
            prevFiles.filter(f => f !== photo.file)
          );
        }
      }
      return prev.filter(p => p.id !== photoId);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('Form submission started');
    console.log('Selected photos:', selectedPhotos);
    console.log('Photo previews:', photoPreview);
    
    try {
      const token = localStorage.getItem('token');
      const formDataToSend = new FormData();
      
      // Add form data
      Object.keys(formData).forEach(key => {
        formDataToSend.append(key, formData[key]);
        console.log(`Added form field: ${key} = ${formData[key]} (type: ${typeof formData[key]})`);
      });
      
      // Add photos
      selectedPhotos.forEach((photo, index) => {
        formDataToSend.append('photos', photo);
        console.log(`Added photo ${index + 1}:`, photo.name, photo.size, photo.type);
      });
      
      console.log('FormData prepared, sending request...');
      
      const response = await fetch('/api/admin/products', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend
      });
      
      console.log('Response received:', response.status, response.statusText);
      
      if (response.ok) {
        const result = await response.json();
        console.log('Product created successfully:', result);
        toast.success('Produit créé avec succès!');
        setShowCreateModal(false);
        resetForm();
        fetchProducts();
      } else {
        const error = await response.json();
        console.error('Server error:', error);
        toast.error(error.error || 'Erreur lors de la création du produit');
      }
    } catch (error) {
      console.error('Error creating product:', error);
      toast.error('Erreur lors de la création du produit');
    }
  };

  const getStockBadge = (stock, minStock) => {
    if (stock <= 0) {
      return <span className="inline-flex items-center justify-center text-center min-w-[90px] px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Rupture de stock</span>;
    } else if (stock <= minStock) {
      return <span className="inline-flex items-center justify-center text-center min-w-[90px] px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Stock faible</span>;
    }
    return <span className="inline-flex items-center justify-center text-center min-w-[90px] px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">En stock</span>;
  };

  const getStockCountChip = (stock, minStock) => {
    const count = Number(stock) || 0;
    const base = 'inline-flex items-center justify-center text-center min-w-[40px] px-2 py-0.5 rounded-full text-xs font-semibold';
    if (count <= 0) {
      return <span className={`${base} bg-red-100 text-red-800`}>{count}</span>;
    } else if (count <= (Number(minStock) || 0)) {
      return <span className={`${base} bg-yellow-100 text-yellow-800`}>{count}</span>;
    }
    return <span className={`${base} bg-green-100 text-green-800`}>{count}</span>;
  };

  const getStatusBadge = (isActive) => {
    return (
      <span className={`inline-flex items-center justify-center text-center min-w-[90px] px-2.5 py-0.5 rounded-full text-xs font-medium ${
        isActive 
          ? 'bg-green-100 text-green-800' 
          : 'bg-red-100 text-red-800'
      }`}>
        {isActive ? 'Actif' : 'Inactif'}
      </span>
    );
  };

  const getFeaturedBadge = (isFeatured) => {
    if (!isFeatured) return null;
    return (
      <span className="inline-flex items-center justify-center text-center min-w-[90px] px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        En vedette
      </span>
    );
  };

  const getNewBadge = (isNew) => {
    if (!isNew) return null;
    return (
      <span className="inline-flex items-center justify-center text-center min-w-[90px] px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
        Nouveau
      </span>
    );
  };

  // Safe price formatting utility
  const safePriceFormat = (price) => {
    if (price === null || price === undefined) return '0.00';
    if (typeof price === 'string') {
      const parsed = parseFloat(price);
      return isNaN(parsed) ? '0.00' : parsed.toFixed(2);
    }
    if (typeof price === 'number') {
      return isNaN(price) ? '0.00' : price.toFixed(2);
    }
    return '0.00';
  };

  const filteredProducts = products.filter(product =>
    product.name_fr.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.name_en.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.sku.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">Gestion des Produits</h1>
          <p className="text-gray-400 mt-2">Gérez votre catalogue de bijoux TOLOU</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold px-4 py-2 rounded-lg transition-colors duration-200 flex items-center space-x-2"
        >
          <Plus size={16} />
          <span>Nouveau Produit</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Recherche</label>
            <div className="relative">
              <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Nom, SKU..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Catégorie</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
            >
              <option value="">Toutes les catégories</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name_fr}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Statut</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
            >
              <option value="">Tous les statuts</option>
              <option value="active">Actif</option>
              <option value="inactive">Inactif</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedCategory('');
                setSelectedStatus('');
                setCurrentPage(1);
              }}
              className="w-full px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors duration-200"
            >
              Réinitialiser
            </button>
          </div>
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-700">
              <tr>
                <th className="text-left py-3 px-4 text-gray-300 font-medium">Photo</th>
                <th className="text-left py-3 px-4 text-gray-300 font-medium">Produit</th>
                <th className="text-left py-3 px-4 text-gray-300 font-medium">Catégorie</th>
                <th className="text-left py-3 px-4 text-gray-300 font-medium">Prix</th>
                <th className="text-left py-3 px-4 text-gray-300 font-medium">Stock</th>
                <th className="text-left py-3 px-4 text-gray-300 font-medium">Statut</th>
                <th className="text-left py-3 px-4 text-gray-300 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filteredProducts.map((product) => {
                try {
                  return (
                    <motion.tr
                      key={product.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="hover:bg-gray-700 transition-colors"
                    >
                      <td className="py-3 px-4">
                        <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-700 flex items-center justify-center">
                          {product.main_photo ? (
                            <img
                              src={product.main_photo}
                              alt={product.name_fr}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div className={`w-full h-full flex items-center justify-center ${product.main_photo ? 'hidden' : 'flex'}`}>
                            <Package size={24} className="text-gray-400" />
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg flex items-center justify-center">
                            <Package size={24} className="text-white" />
                          </div>
                          <div>
                            <div className="text-white font-medium text-xs">{product.name_fr || 'Sans nom'}</div>
                            <div className="text-gray-400 text-sm">{product.sku || 'N/A'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center space-x-2">
                          <Tag size={16} className="text-gray-400" />
                          <span className="text-white">{product.category_name || 'Sans catégorie'}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-white font-medium">
                          {safePriceFormat(product.price)} €
                        </div>
                        <div className="text-gray-400 text-sm">
                          Coût: {safePriceFormat(product.cost_price)} €
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center space-x-2">
                          {getStockBadge(product.in_stock || 0, product.min_stock_level || 0)}
                          {getStockCountChip(product.in_stock || 0, product.min_stock_level || 0)}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center space-x-2">
                          {getStatusBadge(product.is_active)}
                          {getNewBadge(product.is_new)}
                          {getFeaturedBadge(product.is_featured)}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => openEditModal(product)}
                            className="text-blue-400 hover:text-blue-300 transition-colors p-2"
                            title="Modifier"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => setConfirmDelete({ open: true, productId: product.id })}
                            className="text-red-400 hover:text-red-300 transition-colors p-2"
                            title="Supprimer"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  );
                } catch (error) {
                  console.error('Error rendering product:', error, product);
                  return (
                    <tr key={product.id} className="bg-red-900">
                      <td colSpan="7" className="text-center text-red-200 py-4">
                        Erreur d'affichage du produit: {product.id}
                      </td>
                    </tr>
                  );
                }
              })}
            </tbody>
          </table>
        </div>
      </div>

      {filteredProducts.length === 0 && (
        <div className="text-center py-12">
          <Package size={48} className="text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-400 mb-2">
            {searchTerm || selectedCategory || selectedStatus ? 'Aucun produit trouvé' : 'Aucun produit'}
          </h3>
          <p className="text-gray-500">
            {searchTerm || selectedCategory || selectedStatus 
              ? 'Essayez de modifier vos critères de recherche'
              : 'Commencez par créer votre premier produit'
            }
          </p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center">
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-gray-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600 transition-colors"
            >
              Précédent
            </button>
            <span className="px-4 py-2 text-gray-300">
              Page {currentPage} sur {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 bg-gray-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600 transition-colors"
            >
              Suivant
            </button>
          </div>
        </div>
      )}

      {/* Create Product Modal */}
      {showCreateModal && (
        <ProductModal
          title="Créer un nouveau produit"
          formData={formData}
          setFormData={setFormData}
          categories={categories}
          onSubmit={handleSubmit}
          onClose={() => {
            setShowCreateModal(false);
            resetForm();
          }}
          photoPreview={photoPreview}
          handlePhotoSelect={handlePhotoSelect}
          removePhoto={removePhoto}
        />
      )}

      {/* Edit Product Modal */}
      {showEditModal && (
        <ProductModal
          title="Modifier le produit"
          formData={formData}
          setFormData={setFormData}
          categories={categories}
          onSubmit={handleUpdateProduct}
          onClose={() => {
            setShowEditModal(false);
            setEditingProduct(null);
            resetForm();
          }}
          photoPreview={photoPreview}
          handlePhotoSelect={handlePhotoSelect}
          removePhoto={removePhoto}
        />
      )}

      {/* Confirm Delete Modal */}
      <ConfirmModal
        isOpen={confirmDelete.open}
        title="Supprimer le produit"
        message="Êtes-vous sûr de vouloir supprimer ce produit ? Cette action est irréversible."
        confirmText="Supprimer"
        cancelText="Annuler"
        danger
        onCancel={() => setConfirmDelete({ open: false, productId: null })}
        onConfirm={async () => {
          const id = confirmDelete.productId;
          setConfirmDelete({ open: false, productId: null });
          await handleDeleteProduct(id);
        }}
      />
    </div>
  );
};

// Product Modal Component
const ProductModal = ({ title, formData, setFormData, categories, onSubmit, onClose, photoPreview, handlePhotoSelect, removePhoto }) => {
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    // Auto-calc sale_price from discount_percentage or vice-versa
    setFormData(prev => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
      const priceNum = parseFloat(next.price);
      const discountNum = parseFloat(next.discount_percentage);
      const saleNum = parseFloat(next.sale_price);
      if (!isNaN(priceNum) && priceNum > 0) {
        if (name === 'discount_percentage') {
          if (!isNaN(discountNum) && discountNum > 0 && discountNum < 100) {
            next.sale_price = (priceNum * (1 - discountNum / 100)).toFixed(2);
          } else if (discountNum === 0) {
            next.sale_price = '';
          }
        } else if (name === 'sale_price') {
          if (!isNaN(saleNum) && saleNum > 0 && saleNum < priceNum) {
            next.discount_percentage = Math.round(((priceNum - saleNum) / priceNum) * 100).toString();
          } else if (isNaN(saleNum) || saleNum === 0 || saleNum >= priceNum) {
            next.discount_percentage = '';
          }
        } else if (name === 'price') {
          if (!isNaN(discountNum) && discountNum > 0 && discountNum < 100) {
            next.sale_price = (parseFloat(value) * (1 - discountNum / 100)).toFixed(2);
          } else if (!isNaN(saleNum) && saleNum > 0 && saleNum < parseFloat(value)) {
            next.discount_percentage = Math.round(((parseFloat(value) - saleNum) / parseFloat(value)) * 100).toString();
          } else {
            next.sale_price = '';
            next.discount_percentage = '';
          }
        }
      }
      return next;
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-gray-800 rounded-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-700">
          <h2 className="text-2xl font-bold text-white">{title}</h2>
        </div>
        
        <form onSubmit={onSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white border-b border-gray-700 pb-2">Informations de base</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                <input
                  type="text"
                  name="name_fr"
                  value={formData.name_fr}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Nom (Anglais)</label>
                <input
                  type="text"
                  name="name_en"
                  value={formData.name_en}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">SKU</label>
                <input
                  type="text"
                  name="sku"
                  value={formData.sku}
                  onChange={handleChange}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Catégorie</label>
                <select
                  name="category_id"
                  value={formData.category_id}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                >
                  <option value="">Sélectionner une catégorie</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name_fr}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Pricing */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white border-b border-gray-700 pb-2">Prix</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Prix de vente (€)</label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  required
                  step="0.01"
                  min="0"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Pourcentage de réduction (%)</label>
                <input
                  type="number"
                  name="discount_percentage"
                  value={formData.discount_percentage}
                  onChange={handleChange}
                  min="0"
                  max="99"
                  step="1"
                  placeholder="ex: 20"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Prix après réduction (€)</label>
                <input
                  type="number"
                  name="sale_price"
                  value={formData.sale_price}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  placeholder="Calculé automatiquement"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Prix de revient (€)</label>
                <input
                  type="number"
                  name="cost_price"
                  value={formData.cost_price}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Stock */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white border-b border-gray-700 pb-2">Stock</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Stock disponible</label>
                <input
                  type="number"
                  name="in_stock"
                  value={formData.in_stock}
                  onChange={handleChange}
                  required
                  min="0"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Stock minimum</label>
                <input
                  type="number"
                  name="min_stock_level"
                  value={formData.min_stock_level}
                  onChange={handleChange}
                  min="0"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Poids (g)</label>
                <input
                  type="number"
                  name="weight"
                  value={formData.weight}
                  onChange={handleChange}
                  step="0.1"
                  min="0"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Longueur total</label>
                <input
                  type="text"
                  name="dimensions"
                  value={formData.dimensions}
                  onChange={handleChange}
                  placeholder="L x l x h"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Descriptions */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white border-b border-gray-700 pb-2">Descriptions</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Description courte (FR)</label>
                <textarea
                  name="short_description_fr"
                  value={formData.short_description_fr}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Description courte (EN)</label>
                <textarea
                  name="short_description_en"
                  value={formData.short_description_en}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Description complète (FR)</label>
                <textarea
                  name="description_fr"
                  value={formData.description_fr}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Description complète (EN)</label>
                <textarea
                  name="description_en"
                  value={formData.description_en}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Gemme</label>
                <input
                  type="text"
                  name="gemstone"
                  value={formData.gemstone || ''}
                  onChange={handleChange}
                  placeholder="ex: Diamant, Saphir, Émeraude"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Additional Details */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white border-b border-gray-700 pb-2">Détails supplémentaires</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Matière</label>
                <input
                  type="text"
                  name="material"
                  value={formData.material}
                  onChange={handleChange}
                  placeholder="ex: Or 14k, Argent 925"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Couleur</label>
                <input
                  type="text"
                  name="color"
                  value={formData.color}
                  onChange={handleChange}
                  placeholder="ex: Jaune, Blanc, Rose"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Référence</label>
                <input
                  type="text"
                  name="reference"
                  value={formData.reference || ''}
                  onChange={handleChange}
                  placeholder="ex: REF-001"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Options */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white border-b border-gray-700 pb-2">Options</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="h-4 w-4 text-yellow-500 focus:ring-yellow-500 border-gray-600 rounded bg-gray-700"
                />
                <label className="ml-2 text-gray-300">Produit actif</label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="is_new"
                  checked={formData.is_new}
                  onChange={handleChange}
                  className="h-4 w-4 text-yellow-500 focus:ring-yellow-500 border-gray-600 rounded bg-gray-700"
                />
                <label className="ml-2 text-gray-300">Nouveau produit</label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="is_featured"
                  checked={formData.is_featured || false}
                  onChange={handleChange}
                  className="h-4 w-4 text-yellow-500 focus:ring-yellow-500 border-gray-600 rounded bg-gray-700"
                />
                <label className="ml-2 text-gray-300">Produit en vedette</label>
              </div>
            </div>
          </div>

          {/* Photo Upload */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white border-b border-gray-700 pb-2">Photos du produit</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Ajouter des photos (JPG, PNG, WebP - Max 5MB)
                </label>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handlePhotoSelect}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-yellow-500 file:text-gray-900 hover:file:bg-yellow-600"
                />
                <p className="text-sm text-gray-400 mt-1">
                  La première photo sera la photo principale du produit
                </p>
              </div>

              {/* Photo Previews */}
              {photoPreview.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {photoPreview.map((photo, index) => (
                    <div key={photo.id} className="relative group">
                      <img
                        src={photo.preview}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg border-2 border-gray-600"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                        <button
                          type="button"
                          onClick={() => removePhoto(photo.id)}
                          className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-full transition-colors"
                          title={photo.isExisting ? "Supprimer cette photo" : "Retirer cette photo"}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      {index === 0 && (
                        <div className="absolute top-2 left-2 bg-yellow-500 text-gray-900 text-xs px-2 py-1 rounded-full font-semibold">
                          Principal
                        </div>
                      )}
                      {photo.isExisting && (
                        <div className="absolute top-2 right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full font-semibold">
                          Existant
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-yellow-500 text-gray-900 font-semibold rounded-lg hover:bg-yellow-600 transition-colors"
            >
              {title.includes('Créer') ? 'Créer' : 'Mettre à jour'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminProductsPage; 